import React, { useEffect, useState } from "react";
import { ChevronLeftIcon, PencilIcon, XIcon } from "lucide-react-native";

import {
  employmentStatusEnumSchema,
  unemploymentCompensationStatusEnumSchema,
} from "@libs/api/patients";
import { WorkAndIncomeForm } from "@/blocks/WorkAndIncomeForm";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import {
  VnDialog,
  VnDialogContent,
  VnDialogHeader,
  VnDialogPortal,
} from "@/components/VnDialog";
import { VnIcon } from "@/components/VnIcon";
import { VnScrollView } from "@/components/VnScrollView";
import { VnText } from "@/components/VnText/VnText";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import {
  createEmptyIncomeData,
  HouseholdMemberType,
  IncomeTypeEnum,
  incomeTypeOptions,
} from "@/domain/householdMember";
import { formatCurrency } from "@/utils/currency";
import {
  formatHouseholdMemberName,
  hasIncome,
  hasIncomeConfig,
} from "@/utils/householdMember";
import { convertStringToNumber } from "@/utils/string";

type IncomeItemConfigType = {
  incomeType: IncomeTypeEnum;
  fieldName: keyof HouseholdMemberType;
};

const incomeTypeFieldMap: IncomeItemConfigType[] = [
  { incomeType: IncomeTypeEnum.EMPLOYED, fieldName: "grossMonthlyIncome" },
  {
    incomeType: IncomeTypeEnum.UNEMPLOYED,
    fieldName: "unemploymentCompensationAmount",
  },
  {
    incomeType: IncomeTypeEnum.SELF_EMPLOYED,
    fieldName: "selfEmploymentNetMonthlyIncome",
  },
  { incomeType: IncomeTypeEnum.PENSION, fieldName: "pensionMonthlyIncome" },
  { incomeType: IncomeTypeEnum.SSA, fieldName: "ssaMonthlyIncome" },
  { incomeType: IncomeTypeEnum.SSDI, fieldName: "ssdiMonthlyIncome" },
  { incomeType: IncomeTypeEnum.ALIMONY, fieldName: "alimonyMonthlyIncome" },
  {
    incomeType: IncomeTypeEnum.RETIREMENT_INCOME,
    fieldName: "retirementMonthlyIncome",
  },
  {
    incomeType: IncomeTypeEnum.PROPERTY_RENTAL_INCOME,
    fieldName: "propertyRentalMonthlyIncome",
  },
  {
    incomeType: IncomeTypeEnum.WORKERS_COMPENSATION,
    fieldName: "workersCompensationMonthlyIncome",
  },
  { incomeType: IncomeTypeEnum.OTHER, fieldName: "otherMonthlyIncome" },
];
type IncomeItemPropsType = {
  member: HouseholdMemberType;
  onClickEditIncome: (incomeType: IncomeTypeEnum) => void;
};

type UnemployedIncomeSummaryType =
  | { showMonthlyAmount: false }
  | { showMonthlyAmount: true; monthlyAmount: number };

const getUnemployedIncomeSummary = (
  member: HouseholdMemberType,
): UnemployedIncomeSummaryType | null => {
  if (
    member.employmentStatus !== employmentStatusEnumSchema.enum.UNEMPLOYED ||
    member.unemploymentCompensationStatus == null
  ) {
    return null;
  }

  if (
    member.unemploymentCompensationStatus ===
    unemploymentCompensationStatusEnumSchema.enum.NOT_UNEMPLOYMENT_COMPENSATED
  ) {
    return { showMonthlyAmount: false };
  }

  const monthlyAmount = parseHouseholdMemberCurrency(
    member.unemploymentCompensationAmount,
  );
  if (monthlyAmount == null) {
    return { showMonthlyAmount: false };
  }

  return { showMonthlyAmount: true, monthlyAmount };
};

const parseHouseholdMemberCurrency = (
  value: number | string | null,
): number | null => {
  if (value === null) return null;
  if (typeof value === "number") {
    return !isNaN(value) ? value : null;
  }
  return convertStringToNumber(value);
};

type IncomeSummaryRowPropsType = {
  incomeType: IncomeTypeEnum;
  monthlyDetailAmount?: number;
  onEdit: () => void;
};

const IncomeSummaryRow: React.FC<IncomeSummaryRowPropsType> = ({
  incomeType,
  monthlyDetailAmount,
  onEdit,
}) => (
  <VnBox className="bg-background mt-3 gap-2.5 rounded-md p-4">
    <VnBox className="flex flex-row items-center justify-between">
      <VnText
        variant="lg"
        i18nKey={`native:applicationWorkAndIncomeScreen.incomeTypes.${incomeType}`}
      />
      <VnButton variant="ghost" onPress={onEdit}>
        <VnIcon as={PencilIcon} />
      </VnButton>
    </VnBox>
    {monthlyDetailAmount !== undefined ? (
      <VnText
        variant="lg"
        className="text-muted-foreground"
        i18nKey="native:applicationWorkAndIncomeScreen.monthlyIncome"
        i18nOptions={{
          monthlyIncome: formatCurrency(monthlyDetailAmount),
        }}
      />
    ) : null}
  </VnBox>
);

const IncomeItems: React.FC<IncomeItemPropsType> = ({
  member,
  onClickEditIncome,
}) => {
  return (
    <VnBox className="gap-1">
      {incomeTypeFieldMap.map(({ incomeType, fieldName }) => {
        if (incomeType === IncomeTypeEnum.UNEMPLOYED) {
          const summary = getUnemployedIncomeSummary(member);
          if (summary == null) return null;

          return (
            <IncomeSummaryRow
              key={`${member.id}-unemployed`}
              incomeType={incomeType}
              monthlyDetailAmount={
                summary.showMonthlyAmount ? summary.monthlyAmount : undefined
              }
              onEdit={() => onClickEditIncome(incomeType)}
            />
          );
        }

        const numericAmount = parseHouseholdMemberCurrency(
          member[fieldName] as number | string | null,
        );
        if (numericAmount == null) return null;
        return (
          <IncomeSummaryRow
            key={`${member.id}-${fieldName}`}
            incomeType={incomeType}
            monthlyDetailAmount={numericAmount}
            onEdit={() => onClickEditIncome(incomeType)}
          />
        );
      })}
    </VnBox>
  );
};

type WorkAndIncomeCardPropsType = {
  member: HouseholdMemberType;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
};

export const WorkAndIncomeCard: React.FC<WorkAndIncomeCardPropsType> = ({
  member,
  isExpanded,
  onToggleExpand,
  onClose,
}) => {
  const [selectedIncomeType, setSelectedIncomeType] =
    useState<IncomeTypeEnum | null>(null);

  const [isEditMode, setIsEditMode] = useState(false);

  const { mutate: updateHouseholdMember } = useUpdateHouseholdMemberMutation();

  const handleAddNoneIncome = () => {
    const emptyIncomeData = createEmptyIncomeData();
    updateHouseholdMember(
      {
        id: member.id,
        householdMemberData: emptyIncomeData,
      },
      {
        onSuccess: onToggleExpand,
      },
    );
  };

  const handleRemoveNoneIncome = () => {
    updateHouseholdMember({
      id: member.id,
      householdMemberData: { hasIncome: null },
    });
  };

  const handleSelectIncomeType = (incomeType: IncomeTypeEnum) => {
    if (incomeType === IncomeTypeEnum.NONE) {
      // If the member already set no income, do nothing
      if (member.hasIncome === false) {
        onToggleExpand();
        return;
      }
      handleAddNoneIncome();
      return;
    }
    setSelectedIncomeType(incomeType);
  };
  const onClickEditIncome = (incomeType: IncomeTypeEnum) => {
    setSelectedIncomeType(incomeType);
    setIsEditMode(true);
    onToggleExpand();
  };

  useEffect(() => {
    if (!isExpanded) {
      setSelectedIncomeType(null);
      setIsEditMode(false);
    }
  }, [isExpanded]);

  const formattedMemberName = formatHouseholdMemberName(member);
  const hasConfiguredIncomeFlow = hasIncomeConfig(member);

  return (
    <VnCard className="gap-1 rounded-md p-4">
      <VnBox className="flex flex-row items-center justify-between">
        <VnText variant="lgMedium">{formattedMemberName}</VnText>
        <VnButton
          variant={hasConfiguredIncomeFlow ? "outline" : "default"}
          onPress={onToggleExpand}
          i18nKey={
            hasConfiguredIncomeFlow
              ? "native:applicationWorkAndIncomeScreen.addAnotherIncome"
              : "native:applicationWorkAndIncomeScreen.addIncome"
          }
        />
      </VnBox>
      {member.hasIncome === false && (
        <VnBox className="bg-secondary mt-3 gap-2.5 rounded-md p-4">
          <VnBox className="flex flex-row items-center justify-between">
            <VnText
              variant="lg"
              i18nKey="native:applicationWorkAndIncomeScreen.incomeTypes.NONE"
            />
            <VnButton variant="ghost" onPress={handleRemoveNoneIncome}>
              <VnIcon as={XIcon} />
            </VnButton>
          </VnBox>
        </VnBox>
      )}
      {member.hasIncome !== false && hasIncome(member) && (
        <IncomeItems member={member} onClickEditIncome={onClickEditIncome} />
      )}
      {/* Dialog for income form */}
      <VnDialog open={isExpanded} onOpenChange={onToggleExpand}>
        <VnDialogPortal>
          <VnDialogContent className="bg-background m-0 flex h-[100dvh] w-screen max-w-none flex-col gap-0 overflow-y-scroll rounded-none px-0 py-4 sm:m-auto sm:h-auto sm:max-h-[90dvh] sm:max-w-[480px] sm:rounded-lg">
            <VnDialogHeader className="relative shrink-0 flex-row items-center justify-center px-4">
              {selectedIncomeType ? (
                <VnButton
                  variant="outline"
                  size="icon"
                  onPress={() => setSelectedIncomeType(null)}>
                  <VnIcon as={ChevronLeftIcon} />
                </VnButton>
              ) : (
                <VnBox className="h-10 w-10 sm:h-9 sm:w-9" />
              )}
              <VnText variant="headerTwo" className="flex-1 text-center">
                {formattedMemberName}
              </VnText>
            </VnDialogHeader>
            {selectedIncomeType === null ? (
              <VnBox className="px-4">
                <VnText
                  variant="lg"
                  className="text-muted-foreground mb-4 shrink-0 text-center"
                  i18nKey="native:applicationWorkAndIncomeScreen.memberDialog.title"
                />
                <VnScrollView showsVerticalScrollIndicator>
                  <VnBox className="gap-3">
                    {incomeTypeOptions.map(option => (
                      <VnButton
                        key={option.value}
                        variant="outline"
                        i18nKey={option.i18nKey}
                        className="border-border justify-start px-4 py-6"
                        onPress={() => handleSelectIncomeType(option.value)}
                      />
                    ))}
                  </VnBox>
                </VnScrollView>
              </VnBox>
            ) : (
              <WorkAndIncomeForm
                member={member}
                selectedIncomeType={selectedIncomeType}
                onClose={onClose}
                isEditMode={isEditMode}
              />
            )}
          </VnDialogContent>
        </VnDialogPortal>
      </VnDialog>
    </VnCard>
  );
};
